import { DEFAULT_TABLE_PAGE_SIZE } from "@components/common/faro-table/faro-table";
import { useCallback, useMemo, useState } from "react";
import {
  UseTablePaginationProps,
  UseTablePaginationResult,
} from "@hooks/table/use-table-pagination-types";
import {
  calculateTotalCount,
  calculateDisplayIndices,
  calculateLastPage,
  shouldDisableNextButton,
  getPageChangeDirection,
  loadNextPage,
} from "@utils/pagination-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";

/**
 * Unified pagination hook for table components
 *
 * Supports both client-side and server-side pagination with known/unknown totals.
 * Returns all data needed for pagination controls and footer display.
 */
export function useTablePagination<T>({
  mode,
  entities,
  pageIndex,
  setPageIndex,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  serverSidePaginationProps,
  onPageChange,
}: UseTablePaginationProps<T>): UseTablePaginationResult<T> {
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const { handleErrorWithToast } = useErrorContext();

  // Determine effective total count based on mode
  const effectiveTotalCount = useMemo(
    () =>
      calculateTotalCount({
        mode,
        entitiesLength: entities.length,
        totalCount: serverSidePaginationProps?.totalCount,
        hasMoreItems: serverSidePaginationProps?.hasMoreItems,
      }),
    [
      mode,
      entities.length,
      serverSidePaginationProps?.totalCount,
      serverSidePaginationProps?.hasMoreItems,
    ]
  );

  // Calculate pagination metrics
  const { paginatedEntities, from, to, lastPage } = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;

    // Slice entities for current page
    const paginated = entities.slice(start, end);

    // Calculate display indices
    const { from, to } = calculateDisplayIndices({
      pageIndex,
      pageSize,
      paginatedLength: paginated.length,
    });

    // Calculate last page
    const lastPage = calculateLastPage({
      effectiveTotalCount,
      pageSize,
      entitiesLength: entities.length,
      hasMoreItems: serverSidePaginationProps?.hasMoreItems,
    });

    return { paginatedEntities: paginated, from, to, lastPage };
  }, [
    pageIndex,
    pageSize,
    entities,
    effectiveTotalCount,
    serverSidePaginationProps?.hasMoreItems,
  ]);

  // Check if next page should be disabled
  const shouldDisableNextPage = useMemo(
    () =>
      shouldDisableNextButton({
        pageIndex,
        lastPage,
        entitiesLength: entities.length,
        pageSize,
        hasMoreItems: serverSidePaginationProps?.hasMoreItems,
      }),
    [
      pageIndex,
      lastPage,
      entities.length,
      pageSize,
      serverSidePaginationProps?.hasMoreItems,
    ]
  );

  // Handle page changes with smart fetching
  const handlePageChange = useCallback(
    async (newPage: number) => {
      const direction = getPageChangeDirection({
        currentPage: pageIndex,
        newPage,
      });

      // Server-side: Check if we need to fetch more data
      if (mode === "server" && newPage > pageIndex) {
        await loadNextPage({
          newPage,
          pageSize,
          entitiesLength: entities.length,
          setIsLoadingNextPage,
          totalCount: serverSidePaginationProps?.totalCount ?? undefined,
          fetchNextPage: serverSidePaginationProps?.fetchNextPage,
        }).catch((error) => {
          handleErrorWithToast({
            id: `pagination-fetch-error-${Date.now()}`,
            title: "Failed to load next page",
            error,
          });
        });
      }

      // Notify parent and update page
      onPageChange?.(newPage, direction);
      setPageIndex(newPage);
    },
    [
      pageIndex,
      mode,
      onPageChange,
      setPageIndex,
      pageSize,
      serverSidePaginationProps,
      entities.length,
      handleErrorWithToast,
    ]
  );

  // MUI DataGrid compatibility
  const paginationModel = useMemo(
    () => ({
      page: pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  return {
    // Data
    paginatedEntities,

    // Footer display
    footerData: {
      from,
      to,
      rowCount: effectiveTotalCount,
      lastPage,
    },

    // Controls
    paginationModel,
    handlePageChange,
    shouldDisableNextPage,

    // State
    isLoadingNextPage,
  };
}
